import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Search, FilterList, ExpandMore } from '@mui/icons-material';

const EnhancedSearch = ({ 
  onSearch, 
  loading = false, 
  departments = [], 
  roles = [],
  searchQuery = "",
  onSearchQueryChange
}) => {
  const [filters, setFilters] = useState({
    role: '',
    department: '',
    content_type: 'all'
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const handleSearch = () => {
    const searchFilters = {};
    if (filters.role) searchFilters.role = filters.role;
    if (filters.department) searchFilters.department = filters.department;
    if (filters.content_type !== 'all') searchFilters.content_type = filters.content_type;
    
    onSearch(searchFilters);
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      department: '',
      content_type: 'all'
    });
  };

  const hasActiveFilters = filters.role || filters.department || filters.content_type !== 'all';

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: "rgba(255,255,255,0.02)" }}>
      {/* Main Search Bar */}
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search knowledge base..."
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          sx={{ bgcolor: "rgba(255,255,255,0.05)" }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, opacity: 0.5 }} />
          }}
        />
        <Button 
          variant="contained" 
          onClick={handleSearch}
          disabled={loading || !searchQuery.trim()}
          startIcon={<Search />}
        >
          Search
        </Button>
      </Box>

      {/* Filters Accordion */}
      <Accordion 
        expanded={filtersExpanded} 
        onChange={(e, expanded) => setFiltersExpanded(expanded)}
        sx={{ 
          bgcolor: "transparent",
          boxShadow: "none",
          "&:before": { display: "none" }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterList fontSize="small" />
            <Typography variant="body2">
              Advanced Filters
            </Typography>
            {hasActiveFilters && (
              <Chip 
                label="Active" 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            {/* Content Type Filter */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={filters.content_type}
                onChange={(e) => setFilters(prev => ({...prev, content_type: e.target.value}))}
                label="Content Type"
              >
                <MenuItem value="all">All Content</MenuItem>
                <MenuItem value="knowledge">Documents Only</MenuItem>
                <MenuItem value="protocol">Protocols Only</MenuItem>
              </Select>
            </FormControl>

            {/* Department Filter */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={filters.department}
                onChange={(e) => setFilters(prev => ({...prev, department: e.target.value}))}
                label="Department"
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept} value={dept}>
                    {dept || 'Uncategorized'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Role Filter */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={filters.role}
                onChange={(e) => setFilters(prev => ({...prev, role: e.target.value}))}
                label="Role"
              >
                <MenuItem value="">All Roles</MenuItem>
                {roles.map(role => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button 
                size="small" 
                onClick={clearFilters}
                sx={{ ml: 1 }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="caption" sx={{ opacity: 0.7, alignSelf: "center" }}>
                Active filters:
              </Typography>
              {filters.content_type !== 'all' && (
                <Chip 
                  label={`Type: ${filters.content_type}`} 
                  size="small" 
                  onDelete={() => setFilters(prev => ({...prev, content_type: 'all'}))}
                />
              )}
              {filters.department && (
                <Chip 
                  label={`Dept: ${filters.department}`} 
                  size="small" 
                  onDelete={() => setFilters(prev => ({...prev, department: ''}))}
                />
              )}
              {filters.role && (
                <Chip 
                  label={`Role: ${filters.role}`} 
                  size="small" 
                  onDelete={() => setFilters(prev => ({...prev, role: ''}))}
                />
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default EnhancedSearch;